import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Modal, Spin } from 'antd';

import { useQueryClient } from '@tanstack/react-query';
import { useGetPlgAnticipation, usePlgSaveAccess } from 'v2/hooks/usePlgAnticipation';

type PlgAnticipationProps = {
  page: string;
  disabled?: boolean;
};

const PlgAnticipation = ({ page, disabled }: PlgAnticipationProps) => {
  const [open, setOpen] = useState(false);
  const [anticipateInProgress, setAnticipateInProgress] = useState(false);
  const [automaticAnticipateInProgress, setAutomaticAnticipateInProgress] = useState(false);

  const toggleAnticipateProgress = useCallback(() => setAnticipateInProgress(prev => !prev), []);
  const toggleAutomaticAnticipateProgress = useCallback(() => setAutomaticAnticipateInProgress(prev => !prev), []);
  const toggleVisibilityModal = useCallback(() => setOpen(prev => !prev), []);

  const history = useHistory();
  const queryClient = useQueryClient();
  const plgAnticipationQuery = useGetPlgAnticipation();
  const savePlgAccessMutation = usePlgSaveAccess({
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['plgAnticipation'] });
    },
    onError: () => {
      toggleAnticipateProgress();
      toggleAutomaticAnticipateProgress();
      toast.error('Ocorreu um erro! Tente novamente.');
    }
  });

  const someCallToActionInProgress = anticipateInProgress || automaticAnticipateInProgress;

  const saveAccess = async () => {
    await savePlgAccessMutation.mutateAsync(page);
    toggleVisibilityModal();
  };

  const handleClose = () => saveAccess();

  const handleAnticipate = async () => {
    await saveAccess();
    history.push('/producer/financial-statement?a=anticipation');
  };

  const handleAutomaticAnticipate = async () => {
    await saveAccess();
    history.push('/producer/financial-statement?a=automatic-anticipation');
  };

  useEffect(() => {
    if (!plgAnticipationQuery.data) return;
    const { show, screens } = plgAnticipationQuery.data;

    if (show && screens.includes(page)) {
      setOpen(true);
    }
  }, [page, plgAnticipationQuery.data]);

  return (
    <Modal
      open={open && !disabled}
      onCancel={handleClose}
      keyboard={false}
      title='Antecipação de recebíveis'
      footer={
        <div className='flex justify-between'>
          <Button type='text' onClick={handleClose} disabled={someCallToActionInProgress}>
            Agora não
          </Button>
          <div className='flex gap-2'>
            <Button onClick={handleAutomaticAnticipate} disabled={someCallToActionInProgress}>
              {automaticAnticipateInProgress && <Spin size='small' className='mr-1' />}
              Ativar antecipação automática
            </Button>
            <Button type='primary' onClick={handleAnticipate} disabled={someCallToActionInProgress}>
              {anticipateInProgress && <Spin size='small' className='mr-1' />}
              Antecipar meus recebíveis
            </Button>
          </div>
        </div>
      }
    >
      <div
        className='[&_strong]:text-text mb-0 px-4 pt-4 pb-8 [&_p]:text-[rgba(0,0,0,0.54)] [&_p:not(:last-of-child)]:mb-4'
        tabIndex={-1}
      >
        <div className='mb-0 flex items-center gap-4 not-last:mb-6'>
          <div className='border-primary flex border bg-[#0D277208] p-4'>
            <img src='/img/rocket-take-off-icon.svg' alt='Rocket takeoff' className='fill-primary h-8 w-8' />
          </div>
          <div className='text-sm leading-[150%]'>
            <strong>Antecipação de recebíveis</strong>
            <p>
              Acione o poder da antecipação de recebíveis em sua estratégia comercial. Agilize seu fluxo de caixa,
              conquiste liquidez imediata e potencialize seu crescimento no mercado digital. Vamos além juntos!
            </p>
          </div>
        </div>

        <div className='mb-0 flex items-center gap-4 not-last:mb-6'>
          <div className='border-primary flex border bg-[#0D277208] p-4'>
            <img src='/img/gift-icon.svg' alt='Gift' className='fill-primary h-8 w-8' />
          </div>
          <div className='text-sm leading-[150%]'>
            <strong>Taxas reduzidas</strong>
            <p>
              Potencialize seus ganhos com nossas taxas reduzidas. Aumente sua rentabilidade e impulsione sua
              performance financeira.
            </p>
          </div>
        </div>

        <div className='mb-0 flex items-center gap-4 not-last:mb-6'>
          <div className='border-primary flex border bg-[#0D277208] p-4'>
            <img src='/img/cash-coin-icon.svg' alt='Cash coin' className='fill-primary h-8 w-8' />
          </div>
          <div className='text-sm leading-[150%]'>
            <strong>Disponibilidade imediata de valores</strong>
            <p>
              Não espere para prosperar! Com nossa plataforma, tenha disponibilidade imediata de valores. Agilidade para
              impulsionar seus projetos e conquistar resultados rápidos no mundo do marketing digital.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PlgAnticipation;
